import React, { useState, KeyboardEvent, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Flex,
  useColorModeValue,
  Input,
  Select,
  InputGroup,
  Button,
  Box,
  Stack,
} from '@chakra-ui/react';

import { SearchIcon, SmallCloseIcon } from '@chakra-ui/icons';

import SelectSearch from 'components/fields/SelectSearch';

import { apiGetLeadSources, apiGetUserWorkflows } from 'api';

import type { APILeadSourceData, APILeadWorkflowData } from 'types/api.types';

const LeadSearch = ({
  onSearch,
  leadStatues,
  initialSearch = '',
  initialSource = '',
  initialWorkflow = '',
  initialStatus = '',
}: {
  onSearch: (query: string) => void;
  leadStatues?: { label: string; value: string }[];
  initialSearch?: string;
  initialSource?: string;
  initialWorkflow?: string;
  initialStatus?: string;
}) => {
  const inputBg = useColorModeValue('white', 'navy.900');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  // Search states
  const [nameSearch, setNameSearch] = useState(initialSearch);
  const [selectedSource, setSelectedSource] = useState(initialSource);
  const [selectedWorkflow, setSelectedWorkflow] = useState(initialWorkflow);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [searchQuery, setSearchQuery] = useState('');

  // Sync form fields when URL/initial values change (e.g. navigating back with params)
  useEffect(() => {
    setNameSearch(initialSearch);
    setSelectedSource(initialSource);
    setSelectedWorkflow(initialWorkflow);
    setSelectedStatus(initialStatus);
  }, [initialSearch, initialSource, initialWorkflow, initialStatus]);

  // Fetch sources
  const { data: sources = [], isLoading: isSourceLoading } = useQuery({
    queryKey: ['lead-sources'],
    queryFn: apiGetLeadSources,
    staleTime: 5 * 60 * 1000, // 5 minutes
  }) as {
    data: APILeadSourceData[];
    isLoading: boolean;
  };

  // Fetch workflows
  const { data: workflows = [], isLoading: isWorkflowsLoading } = useQuery({
    queryKey: ['user-workflows'],
    queryFn: apiGetUserWorkflows,
    staleTime: 5 * 60 * 1000, // 5 minutes
  }) as {
    data: APILeadWorkflowData[];
    isLoading: boolean;
  };

  const handleSearch = () => {
    const values = {
      search: nameSearch,
      source: selectedSource,
      workflow: selectedWorkflow,
      status: selectedStatus,
    };

    const query = Object.entries(values)
      .filter(([_, value]) => value.length > 0)
      .map(([key, value]) => `${key}=${encodeURIComponent(value.trim())}`)
      .join('&');

    if (query !== searchQuery) {
      onSearch(query);
      setSearchQuery(query);
    }
  };

  const resetSearch = () => {
    setNameSearch('');
    setSelectedSource('');
    setSelectedWorkflow('');
    setSelectedStatus('');
    setSearchQuery('');
    onSearch('');
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box px="20px" pt="20px" borderTop="1px solid" borderColor={borderColor}>
      <Stack
        spacing={4}
        direction={{ base: 'column', lg: 'row' }}
        align="flex-end"
      >
        <Box flex="1">
          <InputGroup>
            <Input
              variant="main"
              placeholder="Search by name, email or phone"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              bg={inputBg}
              size="md"
              onKeyDown={handleKeyDown}
            />
          </InputGroup>
        </Box>

        <Box flex="1">
          <SelectSearch
            options={sources?.map((source) => ({
              value: source.id.toString(),
              label: source.name,
            }))}
            value={selectedSource}
            onChange={(value) => setSelectedSource(value?.toString() || '')}
            placeholder="All sources"
            isDisabled={isSourceLoading}
            allowClear
          />
        </Box>

        <Box flex="1">
          <SelectSearch
            options={workflows.map((workflow) => ({
              value: workflow.id.toString(),
              label: workflow.name,
            }))}
            value={selectedWorkflow}
            onChange={(value) => setSelectedWorkflow(value?.toString() || '')}
            placeholder="All workflows"
            isDisabled={isWorkflowsLoading}
            allowClear
          />
        </Box>

        {leadStatues && (
          <Box flex="1">
            <Select
              variant="main"
              placeholder="All statuses"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              bg={inputBg}
              size="md"
            >
              {leadStatues.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Select>
          </Box>
        )}

        <Flex gap={2}>
          <Button
            onClick={handleSearch}
            leftIcon={<SearchIcon />}
            size="md"
            variant="brand"
          >
            Search
          </Button>
          <Button
            onClick={resetSearch}
            variant="outline"
            leftIcon={<SmallCloseIcon />}
            size="md"
          >
            Reset
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
};
export default React.memo(LeadSearch);
